import { Channel, getMerchantStructureByMerchantId } from '@nemuru-eng/js-api-client';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AgentSelectionComponentState } from '@/components/shared/components/AgentSelectionComponent/types/AgentSelectionComponentState';
import MerchantStructureEntity from '@/entities/MerchantStructureEntity';
import { useCache } from '@/hooks/requests/useCache';
import { useUser } from '@/hooks/useUser';

export const useAgentSelectionComponent = (initialValues: AgentSelectionComponentState) => {
  const { structure, currentChannel, userEntity, isUserAgent, isUserChannel, isUserMerchant, isUserMerchantGroup } =
    useUser();

  const [state, setState] = useState(initialValues);
  const { selectedAgent, selectedChannel, selectedMerchant } = state;

  const merchantIdForFetch = isUserMerchantGroup ? selectedMerchant?.id : undefined;
  const { data: rawFetchedStructure, loading: loadingFetchedStructure } = useCache(
    getMerchantStructureByMerchantId,
    merchantIdForFetch ? { merchantId: merchantIdForFetch } : null,
  );

  const fetchedMerchantStructure = useMemo(
    () => (rawFetchedStructure ? MerchantStructureEntity.getSortedStructure(rawFetchedStructure) : undefined),
    [rawFetchedStructure],
  );

  const lastSyncedMerchantIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!fetchedMerchantStructure) return;
    const fetchedId = fetchedMerchantStructure.merchant.id;
    if (lastSyncedMerchantIdRef.current === fetchedId) return;
    if (selectedMerchant?.id !== fetchedId) return;

    lastSyncedMerchantIdRef.current = fetchedId;
    setState((prev) => ({ ...prev, selectedMerchant: fetchedMerchantStructure.merchant }));
  }, [fetchedMerchantStructure, selectedMerchant?.id]);

  useEffect(() => {
    if (structure && isUserChannel && currentChannel) {
      setState((state) => ({ ...state, selectedChannel: currentChannel }));
    }
  }, [structure, userEntity, currentChannel]);

  useEffect(() => {
    if (!!selectedChannel && !!selectedAgent) {
      setState((state) => ({ ...state, selectedAgent: undefined }));
    }
  }, [selectedChannel]);

  const activeChannels: Channel[] | undefined = isUserMerchantGroup
    ? selectedMerchant?.channels
    : structure?.merchant.channels;

  const showMerchantSelector = !!isUserMerchantGroup;
  const showChannelSelector = !!isUserMerchant || !!isUserMerchantGroup;

  const channelSelectorDisabled = isUserMerchantGroup ? !selectedMerchant : !isUserMerchant;

  const loadingStructure = !!merchantIdForFetch && loadingFetchedStructure;

  return {
    isUserAgent,
    showMerchantSelector,
    showChannelSelector,
    channelSelectorDisabled,
    channels: activeChannels,
    selectedAgent,
    selectedChannel,
    selectedMerchant,
    setState,
    loadingStructure,
  };
};
